import { get } from 'lodash/fp';
import { logger } from '~/api/core/middleware';

import { createRgAPI, createRgFormAPI } from '~/api/core/rgRequest';
import { RGUser } from '~/api/models/auth';
import fb from '~/api/core/facebook';

export const SCOPES = [
  'pages_show_list',
  'ads_management',
  'ads_read',
  // NOTE: business_management causes the tool to :poop_emoji:. Most likely
  // tied to to this bug
  // https://developers.facebook.com/support/bugs/862423444327460/
  'business_management',
  'leads_retrieval',
  'pages_read_engagement',
  'pages_manage_metadata',
  'pages_read_user_content',
  'pages_manage_ads',
  'pages_manage_posts',
  'public_profile',
];

const fbPagesPath = 'me/accounts?fields=name,id,tasks,business&limit=50';
const fbAccountsPath = 'me/adaccounts?fields=name,id,business&limit=50';
const fbPermissionsPath = 'me/permissions';

// Go ahead and create the apis on initialization so that we aren't creating a
// new APIs every request
const loginAPI = createRgFormAPI('/login_new');
const setPageAPI = createRgFormAPI('/set_page_id');
const getPageAPI = createRgFormAPI('/get_page_id');
const setAccountAPI = createRgFormAPI('/set_account_id');
const getAccountAPI = createRgFormAPI('/get_account_id');
const adAccountInfoAPI = createRgFormAPI('/ad_account_info');
const fbPixelAPI = createRgFormAPI('/fb_pixel');

const fbProxyAPI = createRgAPI('/fb_proxy');

const FB_APP_ID = '1654788274772389';

// Recent versions of the FB JS SDK no longer write the fbsr_<app_id> cookie
// (even with `cookie: true` in FB.init), but the backend extracts the signed
// request from that cookie in our `cookies` param to exchange it for the
// user's access token. The cookie's value is exactly the SDK's in-memory
// signedRequest, so synthesize it when the SDK has an active session.
const cookiesForLogin = (): string => {
  try {
    const signedRequest = typeof FB !== 'undefined' && FB.getAuthResponse()?.signedRequest;
    if (signedRequest) {
      return `${document.cookie}; fbsr_${FB_APP_ID}=${signedRequest}`;
    }
  } catch (e) {
    // fall through to plain document.cookie
  }
  return document.cookie;
};

// Rejections from the API layer are usually Response objects; wrapping them
// with `new Error(e)` produces the useless "Error: [object Object]".
const toError = (e: unknown): Error => {
  if (e instanceof Error) {
    return e;
  }
  if (e && typeof (e as Response).status === 'number') {
    const res = e as Response;
    return new Error(`Request failed with status ${res.status}: ${res.url}`);
  }
  return new Error(typeof e === 'string' ? e : JSON.stringify(e));
};

export const loginRG = async (): Promise<RGUser> => {
  try {
    const { data } = await loginAPI.post<RGUser>({
      returnUrl: window.location.href,
      cookies: cookiesForLogin(),
    });

    return data;
  } catch (e) {
    throw toError(e);
  }
};

export const getSelectedPageId = async (): Promise<string | null> => {
  try {
    const { data } = await getPageAPI.post();

    return data;
  } catch (e) {
    throw toError(e);
  }
};

export const setPageId = async (id: string): Promise<string> => {
  try {
    const { data } = await setPageAPI.post({ pageId: id });

    return data;
  } catch (e) {
    throw toError(e);
  }
};

type Business = { id: string; name: string };
export type Page = { id: string; name: string; tasks: string[]; business?: Business };
export const getFacebookPages = async (accessToken: string): Promise<Page[]> => {
  let fbData;
  try {
    fbData = await fb(fbPagesPath, 'get', { access_token: accessToken });
  } catch {
    try {
      const { data } = await fbProxyAPI.get({ path: fbPagesPath });
      return data.data;
    } catch (e) {
      throw toError(e);
    }
  }
  return fbData.data;
};

export const getSelectedAccountId = async (): Promise<string | null> => {
  try {
    const { data } = await getAccountAPI.post();

    return data;
  } catch (e) {
    throw toError(e);
  }
};

export const setAccountId = async (id: string): Promise<string> => {
  try {
    const { data } = await setAccountAPI.post({ accountId: id });

    return data;
  } catch (e) {
    throw toError(e);
  }
};
export const getFacebookAccounts = async (accessToken: string): Promise<{ id: string; name: string }[]> => {
  let fbData;
  try {
    fbData = await fb(fbAccountsPath, 'get', { access_token: accessToken });
  } catch {
    try {
      const { data } = await fbProxyAPI.get({ path: fbAccountsPath });
      return data.data.filter(account => account.business);
    } catch (e) {
      throw toError(e);
    }
  }
  return fbData.data.filter(account => account.business);
};

export const getPermissions = async (accessToken: string): Promise<{ data: { status: string }[] }> => {
  let fbData;
  try {
    fbData = await fb(fbPermissionsPath, 'get', { access_token: accessToken });
  } catch {
    try {
      const { data } = await fbProxyAPI.get({ path: fbPermissionsPath });
      return data.data;
    } catch (e) {
      throw toError(e);
    }
  }
  return fbData;
};

export const getFundingSource = async (accountId: string): Promise<string> => {
  try {
    const { data: { funding_source } } = await adAccountInfoAPI.post({ accountId });

    return funding_source;
  } catch (e) {
    throw toError(e);
  }
};

export const getFbPixel = async (accountId: string): Promise<string> => {
  try {
    const { data: { pixel_id } } = await fbPixelAPI.post({ accountId });

    return pixel_id;
  } catch (e) {
    throw toError(e);
  }
};

const setupFbPixelAPI = createRgFormAPI('/setup_fb_pixel');

// Creates and/or wires up a pixel so it exists both on the ad account and in
// the site's MCP config (the requirement getFbPixel checks for)
export const setupFbPixel = async (accountId: string): Promise<string> => {
  try {
    const { data: { pixel_id } } = await setupFbPixelAPI.post({ accountId });

    return pixel_id;
  } catch (e) {
    throw toError(e);
  }
};
